/**
 * The page's title lives in the app's title bar, not in a block at the top of the page.
 *
 * Every page used to open with its own heading row — "Camera List", a one-line description, and
 * often a couple of buttons — and then a filter bar under it, so the first ~120px of every screen
 * repeated what the sidebar already highlighted. Now:
 *
 *   - The header renders a `<TitleBarSlot>`. With nothing claiming it, it shows the name of the
 *     nav entry that matches the route (`navTitle`), so a page that does nothing still gets a title.
 *   - A page that wants a different title (a camera's name, "Alert #123"), a subtitle, or buttons
 *     next to it renders `<PageTitle title=… subtitle=…>{buttons}</PageTitle>` anywhere in its
 *     tree. It PORTALS into the slot, so the buttons keep their live state and handlers — no
 *     context round-trip, no re-render loop from passing JSX through state.
 *
 * Only one PageTitle should be mounted at a time; if two are, both render (the later one after).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { isNodeActive, type NavNode } from '../nav/types';

interface TitleBarCtx {
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
  claims: number;
  claim: () => () => void;
}

const Ctx = createContext<TitleBarCtx | null>(null);

export function TitleBarProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => n - 1);
  }, []);
  const value = useMemo(() => ({ slot, setSlot, claims, claim }), [slot, claims, claim]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Title + subtitle typography, shared by the fallback and PageTitle so they look identical. */
function TitleText({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <h1 className="truncate text-[15px] font-semibold leading-tight text-foreground">{title}</h1>
      {subtitle && (
        <span className="hidden min-w-0 truncate text-xs text-muted-foreground md:inline">{subtitle}</span>
      )}
    </div>
  );
}

/**
 * Where page titles appear — rendered once, by the header. `fallback` is shown while no
 * PageTitle is mounted (normally `navTitle(...)` for the current route).
 */
export function TitleBarSlot({ fallback, className = '' }: { fallback?: ReactNode; className?: string }) {
  const ctx = useContext(Ctx);
  const ref = useCallback((el: HTMLDivElement | null) => ctx?.setSlot(el), [ctx?.setSlot]);
  if (!ctx) return fallback ? <TitleText title={fallback} /> : null;
  return (
    <div className={`flex min-w-0 flex-1 items-center gap-3 ${className}`}>
      {ctx.claims === 0 && fallback ? <TitleText title={fallback} /> : null}
      <div ref={ref} className="contents" />
    </div>
  );
}

export interface PageTitleProps {
  title: ReactNode;
  /** One short line; hidden below md so the title bar never wraps. */
  subtitle?: ReactNode;
  /** Buttons/toggles shown beside the title, right-aligned within the slot. */
  children?: ReactNode;
}

/**
 * Put this page's title (and any header buttons) in the title bar. Renders nothing in place.
 * Outside a TitleBarProvider it falls back to an inline heading so the page is never untitled.
 */
export function PageTitle({ title, subtitle, children }: PageTitleProps) {
  const ctx = useContext(Ctx);
  const claim = ctx?.claim;
  useEffect(() => (claim ? claim() : undefined), [claim]);
  const content = (
    <>
      <TitleText title={title} subtitle={subtitle} />
      {children && <div className="ml-auto flex shrink-0 items-center gap-2">{children}</div>}
    </>
  );
  if (!ctx) {
    return <div className="mb-3 flex items-center gap-3">{content}</div>;
  }
  return ctx.slot ? createPortal(content, ctx.slot) : null;
}

/**
 * The nav entry naming this route — the most specific match across every tree given (sidebar
 * first, then user menu), so `/cameras/123` still reads "Camera List" and `/alert-rules`
 * does not read "Alerts".
 */
export function navTitle(trees: NavNode[][], pathname: string): string | null {
  let best: { name: string; len: number } | null = null;
  const walk = (nodes: NavNode[]) => {
    for (const n of nodes) {
      if (n.children) walk(n.children);
      if (!n.to || n.section || n.render) continue;
      if (isNodeActive(n, pathname)) {
        const len = n.to.length;
        if (!best || len > best.len) best = { name: n.name, len };
      }
    }
  };
  trees.forEach(walk);
  return best ? (best as { name: string }).name : null;
}

/**
 * Height for a page that fills the screen under the title bar (filter column + results, a
 * board, a wall). Matches the Layout's `<main>` padding: h-14 header + p-2 / sm:p-3 on both edges.
 */
export const PAGE_FILL_H = 'h-[calc(100vh-4.5rem)] sm:h-[calc(100vh-5rem)]';
